import type { HTMLAttributes, ReactNode } from "react";
import { cx } from "@/utils/cx";

export const TableBodyCellAlign = {
	Left: "left",
	Center: "center",
	Right: "right",
} as const;

export type TableBodyCellAlign =
	(typeof TableBodyCellAlign)[keyof typeof TableBodyCellAlign];

export const TableBodyCellState = {
	Default: "default",
	Hover: "hover",
	Selected: "selected",
	Focused: "focused",
} as const;

export type TableBodyCellState =
	(typeof TableBodyCellState)[keyof typeof TableBodyCellState];

export interface TableBodyCellProps
	extends Omit<HTMLAttributes<HTMLDivElement>, "content"> {
	/** Horizontal alignment of cell content (Figma `align`). */
	align?: TableBodyCellAlign;
	/**
	 * Interaction state for design-system previews (Figma `state`).
	 * Live `CustomTable.Cell` keeps this at `default` — row chrome owns hover/selected.
	 */
	state?: TableBodyCellState;
	/** Swappable content slot (Figma `content` instance swap). */
	children?: ReactNode;
}

const alignClassName: Record<TableBodyCellAlign, string> = {
	left: "justify-start",
	center: "justify-center",
	right: "justify-end",
};

const stateClassName: Record<TableBodyCellState, string> = {
	default: "",
	hover: "bg-[rgba(181,71,8,0.05)]",
	selected: "bg-[rgba(181,71,8,0.1)]",
	focused: "z-1 outline-2 outline-offset-[-2px] outline-focus-ring",
};

/**
 * Presentational table body cell shell matching Figma Table/BodyCell (3040:151).
 * Compose with {@link TableCellContent} (or registered `*TextCell` / `AppCell`
 * variants) inside. Live React Aria tables use `CustomTable.Cell` for the same
 * chrome; use this shell (or registered `BodyCell`) outside Aria / in demos.
 */
export function TableBodyCell({
	align = TableBodyCellAlign.Left,
	state = TableBodyCellState.Default,
	children,
	className,
	...props
}: TableBodyCellProps) {
	return (
		<div
			{...props}
			data-align={align}
			data-state={state}
			data-slot="table-body-cell"
			className={cx(
				// Figma Table/BodyCell — h-50, px-20; vertical center via flex (no py —
				// py + tall content grows past 50px the same way table-cells did).
				// No fixed w-[200px]: that forced every live column to ~200px min and
				// inflated horizontal scroll. Demos pass width via className.
				"relative flex h-[50px] max-h-[50px] min-w-0 w-auto max-w-[215px] items-center overflow-x-clip overflow-y-visible px-5 py-0 text-sm font-semibold text-secondary",
				alignClassName[align],
				stateClassName[state],
				className,
			)}
		>
			{children}
		</div>
	);
}
